import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from "react";
import {
    TextInput,
    Checkbox,
    DatePicker,
    MultiSelectionInput,
} from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { FORM_ACTIONS } from "../FormActions";

import styles from "./OrganizationForm.module.sass";
import { checkRoles } from "colbi_web_ui/lib/utils/roleValidator";
import { UserRoles } from "colbi_web_ui/lib/utils/userRoles";
import { adjustForTimezone } from "../../../utils/convertUtils";
import { parseJson } from "../../../utils/utils";
import PackagesTab from "../../../components/Tabs/PackageTab";
import {
    BuildConfigurationComponentContainer,
    BuildMetadataConfigurationContainer,
} from "../../../components/Configuration/configuration";
import { metadataToTemplate } from "../../../utils/json";
import ThemeTab from "../../../components/Tabs/ThemeTab";

const mandatoryChars = `[!@#$%^&*()_+-=[]{};':"|,.<>/?/\\]`;

const OrganizationForm = (props, ref) => {
    const {
        classifiers,
        organizationMerge,
        organizationMetadata,
        data,
        status,
        onChange,
        onSave,
        projectModules,
        getModulesByFileType,
        packages,
    } = props;

    const formRef = useRef();

    const {
        id,
        name,
        organizationID,
        active = false,
        expirationDate,
        organizationsModules,
        metadata,
        limitProjects,
        delimiter,
        Packages: selectedPackages,
        metadataSystem,
    } = data || {};

    const initialDates = {
        expirationDate: expirationDate ? adjustForTimezone(expirationDate) : "",
    };

    const [inputs, setInputs] = useState(initialDates);
    const [delimiterState, setDelimiterState] = useState({});

    const [dupUserMetadata, hasDupUserMetadata] = useState(false);
    const [dupSystemMetadata, hasDupSystemMetadata] = useState(false);

    const [arrayConfigMerge, setArrayConfigMerge] = useState([]);
    const [configMetadata, setConfigMetadata] = useState([]);
    const [selected, setSelected] = useState(0);
    const [appliesTo, setAppliesTo] = useState([]);
    const [metadataFileTypes, setMetadataFileTypes] = useState([]);
    const [appliesToFilter, setAppliesToFilter] = useState([]);
    const [modulesByOrganization, setModulesByOrganization] = useState([]);

    useEffect(() => {
        if (classifiers && classifiers.appliesTo && classifiers.appliesTo.length > 0) {
            setAppliesTo(classifiers.appliesTo);
        }
    }, [classifiers]);

    useEffect(() => {
        if (organizationsModules && organizationsModules.length > 0) {
            setModulesByOrganization(parseJson(organizationsModules));
        }
    }, [organizationsModules]);

    useEffect(() => {
        if (organizationMerge?.length > 0) {
            setArrayConfigMerge(parseJson(organizationMerge));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(organizationMerge)]);

    useEffect(() => {
        if (organizationMetadata?.length > 0) {
            setConfigMetadata(organizationMetadata);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(organizationMetadata)]);

    function transformObject(obj) {
        const result = {};

        for (const key in obj) {
            const value = obj[key];
            const lowercaseKey = value.toLowerCase();

            if (!result[lowercaseKey]) {
                result[lowercaseKey] = [];
            }

            result[lowercaseKey].push(key);
        }

        return result;
    }

    useEffect(() => {
        if (
            projectModules &&
            projectModules.length > 0 &&
            modulesByOrganization &&
            modulesByOrganization.length > 0
        ) {
            const modulesName = [];

            for (const module of modulesByOrganization) {
                const projectModule = projectModules.find((elm) => elm.id === module);

                if (projectModule) {
                    modulesName.push(projectModule);
                }
            }

            const filesByModule = transformObject(JSON.parse(getModulesByFileType || "{}"));

            let currentArrayModules = [];

            for (let module of modulesName) {
                const { name } = module;
                if (filesByModule[name] && filesByModule[name].length > 0) {
                    currentArrayModules.push(...filesByModule[name]);
                }
            }

            if (currentArrayModules && currentArrayModules.length > 0) {
                let resultAppliesToFilter = appliesTo.filter((a) => {
                    return currentArrayModules.includes(a.name.toUpperCase());
                });

                const resultConfigMerge = resultAppliesToFilter.filter((elm) => {
                    const arrayConfigMergeModules = arrayConfigMerge.map(
                        (config) => config.FileType,
                    );
                    return !arrayConfigMergeModules.includes(elm.name);
                });

                const resultConfigMetadata = resultAppliesToFilter.filter((elm) => {
                    const arrayConfigMetadataModules = configMetadata.map(
                        (config) => config.FileType,
                    );
                    return !arrayConfigMetadataModules.includes(elm.name);
                });

                setAppliesToFilter(resultConfigMerge);
                setMetadataFileTypes(resultConfigMetadata);
            }
        }
    }, [
        modulesByOrganization,
        projectModules,
        appliesTo,
        getModulesByFileType,
        arrayConfigMerge,
        configMetadata,
    ]);

    const { i18n, user } = useGlobalValue();

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...(data || {}),
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const [metadataKeys, setMetadataKeys] = useState([]);
    const [metadataValues, setMetadataValues] = useState([]);
    const [systemMetadataKeys, setSystemMetadataKeys] = useState([]);
    const [systemMetadataValues, setSystemMetadataValues] = useState([]);

    useEffect(() => {
        try {
            if (metadataSystem) {
                const parsedSystemMetadata = JSON.parse(metadataSystem);
                setSystemMetadataKeys(Object.keys(parsedSystemMetadata));
                setSystemMetadataValues(Object.values(parsedSystemMetadata));
            }
            if (metadata) {
                const parsedMetadata = JSON.parse(metadata);
                setMetadataKeys(Object.keys(parsedMetadata));
                setMetadataValues(Object.values(parsedMetadata));
            }
        } catch (e) {
            alert("Invalid Json");
        }
    }, [metadataSystem, metadata]);

    useEffect(() => {
        setDelimiterState(JSON.parse(delimiter || "{}"));
    }, [delimiter]);

    useEffect(() => {
        setInputs(initialDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expirationDate]);

    const handleDateChange = (e, fieldName) => {
        setInputs((inputs) => ({
            ...inputs,
            [fieldName]: e,
        }));
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const tabs = ["Organization"];
    const supportOrAdminRoles = [
        UserRoles.ADMIN,
        UserRoles.PP_SUPPORT,
        UserRoles.BACKOFFICE,
        UserRoles.SUPPORT,
    ];
    checkRoles(user, supportOrAdminRoles) &&
        tabs.push("Metadata Config", "Merge Config", "Packages", "Theme");

    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (formRef.current.checkValidity() && !(dupUserMetadata || dupSystemMetadata)) {
                    data["__typename"] = "Organization";
                    data["metadata"] = metadataToTemplate(metadataKeys, metadataValues);
                    data["delimiter"] = JSON.stringify(delimiterState);
                    data["mergeConfig"] = arrayConfigMerge;
                    data["configMetadata"] = configMetadata;
                    for (const props in inputs) {
                        data[`${props}`] = inputs[props];
                    }
                    data["metadataSystem"] = metadataToTemplate(
                        systemMetadataKeys,
                        systemMetadataValues,
                    );
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <div className={styles["tabs"]}>
                {(tabs || []).map((tabName, index) => (
                    <div
                        key={index}
                        className={`${styles["tab"]} ${
                            selected === index ? styles["is-selected"] : ""
                        }`}
                        onClick={() => {
                            setSelected(index);
                        }}
                    >
                        <small>{i18n`${tabName}`}</small>
                    </div>
                ))}
            </div>
            <div className={`${styles["inputs"]} ${styles[`visible-${selected}`]}`}>
                <div>
                    <div className={styles["group-content"]}>
                        <div className={styles["flex"]}>
                            <TextInput
                                className={styles["input"]}
                                name={i18n`Organization ID`}
                                placeholder={i18n`Enter an id`}
                                value={organizationID || ""}
                                required
                                onChange={(e) => {
                                    handleChange(e, "organizationID", e.target.value);
                                }}
                            />
                            <TextInput
                                className={styles["input"]}
                                name={i18n`Organization Name`}
                                placeholder={i18n`Enter a name`}
                                value={name || ""}
                                required
                                onChange={(e) => {
                                    handleChange(e, "name", e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles["input-field"]}>
                        <MultiSelectionInput
                            className={styles["input"]}
                            name={i18n`Modules to Current Organization`}
                            value={(typeof organizationsModules === "string"
                                ? JSON.parse(organizationsModules || "[]")
                                : organizationsModules || []
                            )
                                .map((type) => type)
                                .filter((p) => p)}
                            placeholder={i18n`Select modules to active`}
                            required
                            options={
                                projectModules && projectModules.length
                                    ? projectModules.map(({ id, name }) => ({ id, name: name }))
                                    : []
                            }
                            onChange={(e) => {
                                handleChange(
                                    e,
                                    "organizationsModules",
                                    JSON.stringify(e.target.value),
                                );
                            }}
                        />
                    </div>
                    <div className={styles["input-field"]}>
                        {checkRoles(user, supportOrAdminRoles) ? (
                            <div className={styles["group-content"]}>
                                <BuildMetadataConfigurationContainer
                                    uniqueName={"userMetadata"}
                                    keysArray={metadataKeys}
                                    valuesArray={metadataValues}
                                    keysSetter={setMetadataKeys}
                                    valuesSetter={setMetadataValues}
                                    addButtonMessage={"[+] Add user metadata configuration"}
                                    noMetadataConfigsMessage={"No metadata configurations assigned"}
                                    containerTitle={"User Metadata"}
                                    keyErrorCheck={dupUserMetadata}
                                    keyErrorSetter={hasDupUserMetadata}
                                    dupKeyMessage={
                                        "Key on User Metadata can't have duplicate column values"
                                    }
                                />
                            </div>
                        ) : null}
                        {checkRoles(user, [
                            UserRoles.ADMIN,
                            UserRoles.PP_SUPPORT,
                            UserRoles.BACKOFFICE,
                            UserRoles.SUPPORT,
                        ]) ? (
                            <div className={styles["group-content"]}>
                                <BuildMetadataConfigurationContainer
                                    uniqueName={"systemMetadata"}
                                    keysArray={systemMetadataKeys}
                                    valuesArray={systemMetadataValues}
                                    keysSetter={setSystemMetadataKeys}
                                    valuesSetter={setSystemMetadataValues}
                                    addButtonMessage={"[+] Add system metadata configuration"}
                                    noMetadataConfigsMessage={
                                        "No system metadata configurations assigned"
                                    }
                                    containerTitle={"System Metadata"}
                                    keyErrorCheck={dupSystemMetadata}
                                    keyErrorSetter={hasDupSystemMetadata}
                                    dupKeyMessage={
                                        "Key on System Metadata can't have duplicate column values"
                                    }
                                />
                            </div>
                        ) : null}
                        <div className={styles["group-content"]}>
                            <div className={styles["flex"]}>
                                {checkRoles(user, [UserRoles.ADMIN]) ? (
                                    <TextInput
                                        className={`${styles["input"]}`}
                                        name={i18n`Limit Number of Projects`}
                                        placeholder={i18n`Enter a number`}
                                        value={limitProjects || ""}
                                        type={"number"}
                                        onChange={(e) => {
                                            if (
                                                parseInt(`${e.target.value}`) ||
                                                e.target.value === ""
                                            ) {
                                                handleChange(
                                                    e,
                                                    "limitProjects",
                                                    parseInt(e.target.value),
                                                );
                                            }
                                        }}
                                        onWheel={(e) => e.target.blur()}
                                        min={1}
                                    />
                                ) : null}
                                <DatePicker
                                    className={`${styles["wrap-datepicker"]}`}
                                    name={i18n`Expiration Date`}
                                    value={inputs["expirationDate"]}
                                    onChange={(e) => {
                                        handleDateChange(e, "expirationDate");
                                    }}
                                    dateFormat="dd-MM-yyyy"
                                />
                            </div>
                        </div>
                        <div className={styles["group-content"]}>
                            <div className={styles["flex"]}>
                                <TextInput
                                    className={`${styles["input"]}`}
                                    name={i18n`XML Delimiter`}
                                    placeholder={i18n`Enter a XML delimiter`}
                                    value={
                                        delimiterState && delimiterState.xmlDelimiter
                                            ? delimiterState.xmlDelimiter
                                            : ""
                                    }
                                    maxLength="3"
                                    onChange={(e) => {
                                        setDelimiterState({
                                            ...delimiterState,
                                            xmlDelimiter: e.target.value,
                                        });
                                    }}
                                />
                                <TextInput
                                    className={`${styles["input"]}`}
                                    name={i18n`Source Data Delimiter`}
                                    placeholder={i18n`Enter a Source Data delimiter`}
                                    iconPlaceholder={i18n`The delimiter must be one of these characters: ${mandatoryChars}`}
                                    value={
                                        delimiterState && delimiterState.csvDelimiter
                                            ? delimiterState.csvDelimiter
                                            : ""
                                    }
                                    maxLength="3"
                                    pattern=".*[!@#$%^&*()_+\-=\[\]{};':&quot;\\|,.<>\/?]+.*"
                                    onChange={(e) => {
                                        setDelimiterState({
                                            ...delimiterState,
                                            csvDelimiter: e.target.value,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <Checkbox
                        style={{ marginTop: "10px" }}
                        value={active}
                        onChange={(e) => {
                            handleChange(e, "active", e.target.checked ? 1 : 0);
                        }}
                    >{i18n`Active`}</Checkbox>
                </div>

                {checkRoles(user, supportOrAdminRoles) ? (
                    <>
                        <div className={styles["group-content"]}>
                            <BuildConfigurationComponentContainer
                                fileType={metadataFileTypes}
                                target={configMetadata}
                                setter={setConfigMetadata}
                            />
                        </div>
                        <div>
                            <div className={styles["group-content"]} name={appliesToFilter.length}>
                                <BuildConfigurationComponentContainer
                                    fileType={appliesToFilter}
                                    target={arrayConfigMerge}
                                    setter={setArrayConfigMerge}
                                />
                            </div>
                        </div>
                        <div>
                            <PackagesTab
                                packages={packages}
                                selectedPackages={selectedPackages}
                                handleChange={handleChange}
                                entitieID={id}
                                styles={styles}
                            />
                        </div>
                        <div>
                            <ThemeTab data={data} handleChange={handleChange} styles={styles} />
                        </div>
                    </>
                ) : null}
            </div>
        </form>
    ) : null;
};

const OrganizationFormRef = forwardRef(OrganizationForm);

OrganizationFormRef.queries = (args) => {
    return [
        {
            resource: "classifiers",
            body: `
                    severity{id, name},
                    level{id, name},
                    referenceType{id, name},
                    appliesToDataType{id, name},
                    appliesTo{id, name},
                    appliesToVersion{id, name}
                `,
        },
        {
            resource: "organizationMerge",
            args: { organizationId: args.id },
            body: `
                    ID,        
                    FileType
                    Description
                    Configuration
                    `,
        },
        {
            resource: "organizationMetadata",
            args: { organizationId: args.id },
            body: `
                    ID,        
                    FileType
                    Description
                    Configuration
                    `,
        },
        {
            resource: "getModulesByFileType",
        },
        {
            resource: "available_project_modules",
            body: "id, name",
        },
        {
            resource: "getPackages",
            body: "id,name",
        },
        args && args.id
            ? {
                  resource: "organization",
                  args: { [`organizationId`]: args.id },
                  body: "__typename,id,organizationID,active,expirationDate,name,organizationsModules,metadata,limitProjects,delimiter,metadataSystem, Packages{id, packageId, organizationId, quantity, inherit}, mosaicThemeConfig",
              }
            : undefined,
    ].filter((q) => q);
};

OrganizationFormRef.dataMapper = [
    "classifiers",
    "organizationMerge",
    "organizationMetadata",
    "getModulesByFileType",
    "projectModules",
    "packages",
    "data",
];

OrganizationFormRef.actions = [FORM_ACTIONS.SAVE];
export default OrganizationFormRef;
